import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import { DCR_API_BASE_SERVER_URL } from 'config';
import { PayloadAttachment } from '../types';

interface OriginalDocumentPanelProps {
  agentActivityLogId: string;
  attachments: PayloadAttachment[];
}

export const OriginalDocumentPanel = ({ agentActivityLogId, attachments }: OriginalDocumentPanelProps) => {
  const [index, setIndex] = useState(() => {
    const primaryIndex = attachments.findIndex(a => a.isPrimary);
    return primaryIndex >= 0 ? primaryIndex : 0;
  });
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const attachment = attachments[index];

  useEffect(() => {
    if (!attachment) return undefined;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setViewUrl(null);
      try {
        // A presigned S3 URL with an inline Content-Disposition — embedding it directly (rather than
        // blob-fetching through this server) avoids the deployed environment's CSP blocking embedded
        // blob: URLs, same strategy as the Document Viewer page.
        const url = `${DCR_API_BASE_SERVER_URL}/assistant-activity-log/attachments/view-url?agentActivityLogId=${agentActivityLogId}&fileName=${encodeURIComponent(attachment.fileName)}`;
        const response = await axios.get<{ viewUrl: string; mimeType: string }>(url);
        if (!cancelled) {
          setViewUrl(response.data.viewUrl);
          setContentType(response.data.mimeType);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [agentActivityLogId, attachment]);

  if (!attachment) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Typography component="div" variant="subtitle1" fontWeight="bold" gutterBottom>
          Original Document
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No source document available for this activity.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography component="div" variant="subtitle1" fontWeight="bold">
          Original Document
        </Typography>
      </Stack>
      <Box sx={{ flex: 1, overflow: 'auto', px: 1, pb: 1 }}>
        {loading || !viewUrl ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', minHeight: 400 }}>
            <Typography variant="body2" color="text.secondary">
              Loading document…
            </Typography>
          </Stack>
        ) : contentType === 'application/pdf' ? (
          <embed src={viewUrl} type="application/pdf" width="100%" height="900px" />
        ) : contentType?.startsWith('image/') ? (
          <img src={viewUrl} alt={attachment.fileName} style={{ width: '100%' }} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Preview isn&apos;t available for this file type.
          </Typography>
        )}
      </Box>
      {attachments.length > 1 && (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ py: 1 }}>
          <IconButton size="small" disabled={index === 0} onClick={() => setIndex(i => i - 1)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            {index + 1}/{attachments.length}
          </Typography>
          <IconButton size="small" disabled={index === attachments.length - 1} onClick={() => setIndex(i => i + 1)}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}
    </Paper>
  );
};
